// src/pages/admin/AdminProyecto/TabFinanciero.js
//
// Tab 2: Análisis financiero — supuestos, charts, proyección 24 meses,
// break-even, escenarios, conclusiones.

import React, { useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, Chip, Divider, Alert,
} from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';

import {
  calcularProyeccion, mesBreakeven, formatCOP, formatFull,
} from './modeloFinanciero';

const BRAND = { primary: '#2E7D32', dark: '#1B5E20', accent: '#F57C00', danger: '#C62828' };

const Metric = ({ label, value, detail, color = BRAND.primary }) => (
  <Paper elevation={0} sx={{
    p: 2, borderRadius: 2, borderLeft: 4, borderColor: color, bgcolor: '#fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)', height: '100%',
  }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 700, mt: 0.5 }}>
      {value}
    </Typography>
    {detail && (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        {detail}
      </Typography>
    )}
  </Paper>
);

const H2 = ({ children }) => (
  <Typography variant="h5" sx={{
    color: BRAND.dark, fontWeight: 700, mt: 4, mb: 2, pb: 1,
    borderBottom: 2, borderColor: BRAND.primary,
  }}>
    {children}
  </Typography>
);

const H3 = ({ children }) => (
  <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 600, mt: 3, mb: 1.5 }}>
    {children}
  </Typography>
);

const TermBox = ({ children }) => (
  <Box sx={{
    bgcolor: '#E1F5FE', borderLeft: 3, borderColor: '#0277BD',
    p: 1.5, my: 1.5, borderRadius: 1, color: '#01579B', fontSize: 14,
  }}>
    {children}
  </Box>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 0, listStyle: 'none' }}>
    {items.map((it, i) => (
      <Box component="li" key={i} sx={{
        py: 0.75, pl: 4, position: 'relative', borderBottom: 1, borderColor: '#EEE', fontSize: 14,
        '&::before': { content: '"▸"', position: 'absolute', left: 8, color: BRAND.primary, fontWeight: 'bold' },
        '&:last-child': { borderBottom: 0 },
      }} dangerouslySetInnerHTML={{ __html: it }} />
    ))}
  </Box>
);

export default function TabFinanciero() {
  const proyeccion = useMemo(() => calcularProyeccion(24), []);
  const be = useMemo(() => mesBreakeven(proyeccion), [proyeccion]);

  // Datos para recharts (con etiqueta corta del mes)
  const dataChart = proyeccion.map(m => ({
    ...m,
    label: 'Mes ' + m.mes,
  }));

  return (
    <Box>
      {/* Resumen ejecutivo */}
      <Card sx={{
        background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
        borderLeft: 4, borderColor: BRAND.primary, mb: 3,
      }}>
        <CardContent>
          <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 700, mb: 1.5 }}>
            Resumen ejecutivo
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            HolaVeci opera con un modelo de doble ingreso: una <strong>suscripción mensual del tendero</strong>
            ($30.000 con IVA) y una <strong>tarifa por pedido del veci</strong> ($200 con IVA). Este análisis
            proyecta cuándo el negocio deja de perder plata y cuánto crece en dos años operando desde Bucaramanga
            con un modelo austero.
          </Typography>

          <TermBox>
            <strong>Revenue</strong> son los ingresos que entran al negocio cada mes. "Revenue mensual de $4M"
            = el negocio factura $4 millones ese mes.
          </TermBox>

          <TermBox>
            <strong>Punto de equilibrio (break-even):</strong> momento en que los ingresos cubren todos los gastos
            (incluyendo desarrollo). Antes se necesita capital externo; después el negocio se sostiene solo.
          </TermBox>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Metric color="#388E3C" label="Punto de equilibrio" value={be ? 'Mes ' + be.mes : '—'} detail="~90 tenderos activos" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric label="Pedidos necesarios" value="~5.850" detail="mensuales en el break-even" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric label="Revenue mes 24" value="$27M" detail="COP mensuales al año 2" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric color={BRAND.accent} label="Capital de arranque" value="$10M" detail="Hasta break-even" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Supuestos */}
      <H2>Supuestos del modelo</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          Supuestos conservadores. Todos los valores en pesos colombianos (COP). Arranque austero desde Bucaramanga
          con el founder-desarrollador en la adquisición inicial.
        </Typography>

        <H3>¿De dónde salen los ingresos?</H3>
        <TermBox>
          <strong>Cuidado con el IVA:</strong> Cuando se cobra $30.000 al tendero, no todo entra como ingreso. El 19%
          es IVA que se remite a la DIAN. Del cobro de $30.000, el negocio se queda con $25.210. Igual la tarifa
          de $200: neto $168.
        </TermBox>

        <BulletList items={[
          '<strong>Suscripción del tendero:</strong> $30.000/mes con IVA — ingreso real: $25.210',
          '<strong>Tarifa del veci por pedido:</strong> $200 con IVA — ingreso real: $168',
          '<strong>Primeros 3 pedidos del veci son gratis</strong> (bienvenida asumida por HolaVeci)',
        ]} />

        <H3>¿Cuántos tenderos se afilian cada mes?</H3>
        <BulletList items={[
          '<strong>Meses 1-2:</strong> 8-10 tenderos nuevos por mes (afiliación manual)',
          '<strong>Meses 3-4:</strong> 12-15 tenderos nuevos por mes (aparecen primeros aliados)',
          '<strong>Meses 5-8:</strong> 18-28 tenderos nuevos por mes (concesionarios activos)',
          '<strong>Meses 9-12:</strong> 30-40 tenderos nuevos por mes (piloto consolidado)',
          '<strong>Meses 13-24:</strong> 40-50 tenderos nuevos por mes (expansión a otros barrios)',
        ]} />

        <TermBox>
          <strong>Churn:</strong> porcentaje de tenderos que se van cada mes. Si de 100 activos se van 5 al mes
          siguiente, churn = 5%. Es crítico: "come" el crecimiento.
        </TermBox>
        <Typography variant="body2"><strong>Churn mensual asumido:</strong> 5% al inicio, bajando a 3% cuando el producto madura.</Typography>

        <H3>Pedidos por tendero por mes</H3>
        <BulletList items={[
          '<strong>Meses 1-2:</strong> 30 pedidos/mes por tendero',
          '<strong>Meses 3-4:</strong> 40 pedidos/mes',
          '<strong>Meses 5-6:</strong> 50 pedidos/mes',
          '<strong>Meses 7-9:</strong> 65 pedidos/mes',
          '<strong>Meses 10-15:</strong> 80-95 pedidos/mes',
          '<strong>Meses 16-24:</strong> 100-120 pedidos/mes',
        ]} />

        <H3>Costos operativos</H3>
        <BulletList items={[
          '<strong>Meses 1-3:</strong> $1.800.000/mes ($1.500.000 desarrollo + $300.000 infraestructura)',
          '<strong>Meses 4-6:</strong> $1.900.000/mes',
          '<strong>Meses 7-12:</strong> $2.200.000/mes',
          '<strong>Meses 13-18:</strong> $3.000.000/mes (agrega ayudante part-time)',
          '<strong>Meses 19-24:</strong> $4.000.000/mes (pequeño equipo consolidado)',
          '<strong>Comisiones a concesionarios:</strong> 20% de la suscripción neta = $5.042 por tendero activo',
        ]} />
      </CardContent></Card>

      {/* Chart 1: revenue vs costos */}
      <H2>Revenue mensual vs. costos operativos</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          La <strong style={{ color: BRAND.primary }}>línea verde</strong> es cuánto factura HolaVeci cada mes.
          La <strong style={{ color: BRAND.danger }}>línea roja</strong> es cuánto gasta cada mes. Donde la verde
          cruza la roja hacia arriba se alcanza el break-even.
        </Typography>
        <Box sx={{ height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dataChart} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={formatCOP} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => formatFull(v)} />
              <Legend />
              <Line type="monotone" dataKey="revenueTotal" name="Revenue mensual" stroke={BRAND.primary} strokeWidth={3} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="costos"       name="Costos operativos" stroke={BRAND.danger}  strokeWidth={3} strokeDasharray="5 5" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent></Card>

      {/* Chart 2: tenderos activos */}
      <H2>Tenderos activos acumulados</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Cuántos tenderos están efectivamente pagando cada mes. La barra <strong style={{ color: BRAND.accent }}>naranja</strong> marca
          el mes de break-even.
        </Typography>
        <Box sx={{ height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => v.toLocaleString('es-CO') + ' tenderos'} />
              <Bar dataKey="tenderosActivos" name="Tenderos activos"
                fill={BRAND.primary}
                shape={(props) => {
                  const isBE = props.payload.mes === be?.mes;
                  const isPre = be && props.payload.mes < be.mes;
                  const color = isBE ? BRAND.accent : isPre ? '#A5D6A7' : BRAND.primary;
                  return <rect x={props.x} y={props.y} width={props.width} height={props.height} fill={color} rx={4} />;
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent></Card>

      {/* Chart 3: breakdown */}
      <H2>¿De dónde viene la plata? Suscripciones vs. tarifas por pedido</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          La <strong style={{ color: BRAND.primary }}>suscripción del tendero</strong> es predecible.
          La <strong style={{ color: BRAND.accent }}>tarifa por pedido</strong> crece más rápido porque depende de más tenderos + más pedidos por tendero.
        </Typography>
        <Box sx={{ height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={formatCOP} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => formatFull(v)} />
              <Legend />
              <Bar dataKey="revSuscripcion" name="Suscripciones"      stackId="a" fill={BRAND.primary} />
              <Bar dataKey="revTarifas"     name="Tarifas por pedido" stackId="a" fill={BRAND.accent}  />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent></Card>

      {/* Tabla proyección 24 meses */}
      <H2>Proyección mes a mes en detalle</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Tabla completa. La <strong>fila verde</strong> marca el break-even. Las <strong>naranjas</strong> son hitos (mes 3, 6, 12, 18, 24).
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: BRAND.primary }}>
                {['Mes', 'Tenderos', 'Pedidos', 'Suscripción', 'Tarifas', 'Total', 'Costos', 'Balance', 'Acumulado'].map(h =>
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {proyeccion.map(m => {
                const esBE = m.mes === be?.mes;
                const esHito = [3, 6, 12, 18, 24].includes(m.mes);
                const bg = esBE ? '#C8E6C9' : esHito ? '#FFF3E0' : (m.mes % 2 === 0 ? '#FAFAFA' : '#fff');
                return (
                  <TableRow key={m.mes} sx={{ bgcolor: bg, '& td': { fontWeight: esBE ? 700 : (esHito ? 600 : 400) } }}>
                    <TableCell><strong>{m.mes}</strong></TableCell>
                    <TableCell>{m.tenderosActivos}</TableCell>
                    <TableCell>{m.pedidosTotales.toLocaleString('es-CO')}</TableCell>
                    <TableCell>{formatCOP(m.revSuscripcion)}</TableCell>
                    <TableCell>{formatCOP(m.revTarifas)}</TableCell>
                    <TableCell><strong>{formatCOP(m.revenueTotal)}</strong></TableCell>
                    <TableCell>{formatCOP(m.costos)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={formatCOP(m.balance)}
                        sx={{ bgcolor: m.balance >= 0 ? '#C8E6C9' : '#FFCDD2', color: m.balance >= 0 ? '#388E3C' : BRAND.danger, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={formatCOP(m.balanceAcumulado)}
                        sx={{ bgcolor: m.balanceAcumulado >= 0 ? '#C8E6C9' : '#FFCDD2', color: m.balanceAcumulado >= 0 ? '#388E3C' : BRAND.danger, fontWeight: 600 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </CardContent></Card>

      {/* Punto de equilibrio */}
      <H2>Análisis del punto de equilibrio</H2>
      <Card sx={{ background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)', borderLeft: 4, borderColor: BRAND.primary }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 600, mb: 2 }}>
            ¿Cuántos tenderos y ventas necesita para dejar de perder plata?
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><Metric color="#388E3C" label="Tenderos activos"    value="~90"    detail="pagando cada mes" /></Grid>
            <Grid item xs={12} sm={6} md={3}><Metric color="#388E3C" label="Pedidos mensuales"   value="~5.850" detail="con tarifa cobrada" /></Grid>
            <Grid item xs={12} sm={6} md={3}><Metric color="#388E3C" label="Ingresos mensuales"  value="$3.3M"  detail="para cubrir $1.9M de costos" /></Grid>
            <Grid item xs={12} sm={6} md={3}><Metric                 label="Timing esperado"     value={be ? 'Mes ' + be.mes : '—'} detail="7 meses desde el lanzamiento" /></Grid>
          </Grid>
          <Typography variant="body1" sx={{ mt: 3 }}>
            <strong>En palabras sencillas:</strong> con 90 tenderos activos generando 65 pedidos/mes cada uno,
            HolaVeci factura suficiente para cubrir todos los gastos y quedar en positivo. A partir de ahí, cada
            tendero nuevo genera flujo positivo directamente.
          </Typography>
        </CardContent>
      </Card>

      {/* Escenarios */}
      <H2>¿Y si las cosas no van tan bien? Análisis de escenarios</H2>
      <Card><CardContent>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: BRAND.primary }}>
              {['Escenario', 'Ritmo de afiliación', 'Pedidos por tendero', 'Break-even', 'Ingresos mes 24'].map(h =>
                <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><Chip size="small" label="Pesimista" sx={{ bgcolor: '#FFCDD2', color: BRAND.danger, fontWeight: 600 }} /></TableCell>
              <TableCell>50% del base</TableCell><TableCell>40/mes máximo</TableCell><TableCell>Mes 13-15</TableCell><TableCell>~$14M</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#FFF3E0' }}>
              <TableCell><Chip size="small" label="Base (este análisis)" sx={{ bgcolor: '#E1F5FE', color: '#0277BD', fontWeight: 600 }} /></TableCell>
              <TableCell>8→50/mes escalonado</TableCell><TableCell>30→120/mes</TableCell><TableCell><strong>Mes 7</strong></TableCell><TableCell><strong>~$27M</strong></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Chip size="small" label="Optimista" sx={{ bgcolor: '#C8E6C9', color: '#388E3C', fontWeight: 600 }} /></TableCell>
              <TableCell>150% del base</TableCell><TableCell>150/mes tardíos</TableCell><TableCell>Mes 5-6</TableCell><TableCell>~$42M</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Conclusiones */}
      <H2>Conclusiones estratégicas</H2>
      <Card><CardContent>
        <H3>1. El déficit acumulado hasta break-even se cubre con el capital de arranque</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Con $10M de capital inicial se cubre todo el déficit acumulado antes del break-even más un buffer razonable.
          NO se necesitan rondas grandes posteriores: HolaVeci arranca, y de ahí en adelante se sostiene solo.
        </Typography>

        <H3>2. La variable más importante es "pedidos por tendero", no "cantidad de tenderos"</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          50 tenderos con 100 pedidos/mes generan casi lo mismo que 100 tenderos con 50 pedidos/mes. Pero los 50 cuestan
          la mitad en soporte y adquisición. <strong>Estrategia enfocada en profundidad, no cantidad.</strong>
        </Typography>

        <H3>3. Después del mes 7 el negocio se autofinancia</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Cada tendero nuevo genera flujo positivo. La expansión a otros barrios y ciudades se paga con ingresos propios,
          sin más inversión externa.
        </Typography>

        <H3>4. El mes 4 es momento clave de decisión</H3>
        <Typography variant="body2">
          Si en el mes 4 hay ~40 tenderos activos con revenue de ~$1.5M, el modelo va según plan. Si los números están
          significativamente por debajo, hay tiempo para ajustar antes de agotar el capital inicial.
        </Typography>
      </CardContent></Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Nota metodológica:</strong> Este análisis usa supuestos conservadores. Los valores de suscripción ($30.000)
        y tarifa ($200) están con IVA incluido; el ingreso neto es la base gravable menos 19%. Los costos operativos
        incluyen el desarrollo del founder ($1.5M/mes durante los primeros 3 meses). Estas proyecciones son planeación
        estratégica, no una garantía.
      </Alert>
    </Box>
  );
}
